import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText, Plus, Save, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { purchaseOrderService, type PurchaseOrderSavePayload, type PurchaseOrderSaveLine } from '@/services/purchaseOrderService'
import { grnService } from '@/services/grnService'
import { showSaveSuccess, showSaveError } from '@/lib/swal'

type OrderLine = {
  id: number
  itemId: string
  taxId: string
  quantity: number
  unitPrice: number
  discountPercent: number
  taxPercent: number
}

const newLine = (defaultTax?: { id: number; percent: number }): OrderLine => ({
  id: 0,
  itemId: '',
  taxId: defaultTax ? String(defaultTax.id) : '',
  quantity: 0,
  unitPrice: 0,
  discountPercent: 0,
  taxPercent: defaultTax?.percent || 0,
})

const today = () => new Date().toISOString().slice(0, 10)

export const InventoryPurchaseOrderEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = Boolean(id && id !== 'new')
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)

  const [orderDate, setOrderDate] = useState(today())
  const [expectedDate, setExpectedDate] = useState(today())
  const [reference, setReference] = useState('')
  const [vendorId, setVendorId] = useState('')
  const [warehouseId, setWarehouseId] = useState('')
  const [deliveryAddress, setDeliveryAddress] = useState('')
  const [paymentTerms, setPaymentTerms] = useState('')
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  /** From API on edit — resent as AttachmentPath when not replacing file */
  const [savedAttachmentPath, setSavedAttachmentPath] = useState('')
  const [orderCode, setOrderCode] = useState('')
  const [createdDate, setCreatedDate] = useState(() => new Date().toISOString())
  const [createdBy, setCreatedBy] = useState(0)
  const [vendors, setVendors] = useState<Array<{ id: number; name: string }>>([])
  const [warehouses, setWarehouses] = useState<Array<{ id: number; name: string }>>([])
  const [paymentTermOptions, setPaymentTermOptions] = useState<Array<{ id: number; name: string }>>([])
  const [items, setItems] = useState<Array<{ id: number; code: string; name: string; tax: number }>>([])
  const [taxRates, setTaxRates] = useState<Array<{ id: number; name: string; percent: number }>>([])
  const [lines, setLines] = useState<OrderLine[]>(Array.from({ length: 3 }, () => newLine()))
  const [deletedRowIndices, setDeletedRowIndices] = useState<Set<number>>(new Set())
  const [hasGrn, setHasGrn] = useState(false)

  const getDefaultTaxRate = (rates: Array<{ id: number; name: string; percent: number }>) => {
    const byPercent = rates.find((r) => Math.abs((r.percent || 0) - 15) < 0.01)
    if (byPercent) return byPercent
    const vat = rates.find((r) => /vat/i.test(String(r.name || '')))
    if (vat) return vat
    return rates[0] ?? null
  }

  useEffect(() => {
    if (!branchId) return
    const loadLookups = async () => {
      try {
        const [vendorsRes, warehousesRes, paymentTermsRes, itemsRes, taxRatesRes] = await Promise.all([
          purchaseOrderService.getVendors(branchId),
          purchaseOrderService.getWarehouses(branchId),
          purchaseOrderService.getPaymentTerms(),
          purchaseOrderService.getItems(branchId),
          purchaseOrderService.getTaxRates(branchId),
        ])
        setVendors(vendorsRes.filter((x) => x.id > 0))
        setWarehouses(warehousesRes.filter((x) => x.id > 0))
        setPaymentTermOptions(paymentTermsRes.filter((x) => x.id > 0))
        setItems(itemsRes.filter((x) => x.id > 0))
        const filteredTaxRates = taxRatesRes.filter((x) => x.id > 0)
        setTaxRates(filteredTaxRates)

        const defaultTax = getDefaultTaxRate(filteredTaxRates)
        if (defaultTax) {
          setLines((prev) =>
            prev.map((line) => {
              if (line.taxId) return line
              return { ...line, taxId: String(defaultTax.id), taxPercent: defaultTax.percent || 0 }
            })
          )
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t('purchaseOrders.failedToLoadDropdowns'))
      }
    }
    void loadLookups()
  }, [branchId])

  useEffect(() => {
    if (!isEdit || !id) return
    const loadForEdit = async () => {
      try {
        const orderId = Number(id)
        const [master, lineRows, orderLines] = await Promise.all([
          purchaseOrderService.getById(orderId),
          purchaseOrderService.getLinesByOrderId(orderId),
          grnService.getOrderLines(orderId).catch(() => ({ grnLines: [] as Record<string, unknown>[] })),
        ])
        setHasGrn(orderLines.grnLines.length > 0)

        const toInputDate = (value?: string) => {
          if (!value) return today()
          const d = new Date(value)
          if (Number.isNaN(d.getTime())) return today()
          return d.toISOString().slice(0, 10)
        }

        setReference(master.reference || '')
        setVendorId(master.vendorId ? String(master.vendorId) : '')
        setWarehouseId(master.warehouseId ? String(master.warehouseId) : '')
        setOrderDate(toInputDate(master.orderDate))
        setExpectedDate(toInputDate(master.deliveryDate))
        setDeliveryAddress(master.deliveryAddress || '')
        setPaymentTerms(master.paymentTerms ? String(master.paymentTerms) : '')
        setOrderCode(master.code || '')
        setSavedAttachmentPath(master.attachmentPath || '')
        if (master.createdDate) setCreatedDate(master.createdDate)
        if (master.createdBy != null) setCreatedBy(Number(master.createdBy))

        const filledLines = lineRows.filter((line) => {
          const hasItem = Number(line.itemId || 0) > 0
          const hasQty = Number(line.quantity || 0) > 0
          const notDeleted = String(line.isDeleted || 'false').toLowerCase() !== 'true'
          // Edit screen should show only genuinely filled rows.
          return notDeleted && hasItem && hasQty
        })

        if (filledLines.length > 0) {
          setLines(
            filledLines.map((line) => ({
              id: Number(line.id || 0),
              itemId: line.itemId ? String(line.itemId) : '',
              taxId: line.tax ? String(line.tax) : '',
              quantity: Number(line.quantity || 0),
              unitPrice: Number(line.unitPrice || 0),
              discountPercent: 0,
              taxPercent: Number(line.taxPercent || 0),
            }))
          )
          setDeletedRowIndices(new Set())
        } else {
          const defaultTax = getDefaultTaxRate(taxRates)
          setLines([newLine(defaultTax || undefined)])
          setDeletedRowIndices(new Set())
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t('purchaseOrders.failedToLoadForEdit'))
      }
    }
    void loadForEdit()
  }, [isEdit, id])

  const totals = useMemo(() => {
    const activeLines = lines.filter((_, index) => !deletedRowIndices.has(index))
    const subTotal = activeLines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0)
    const discount = 0
    const taxable = subTotal
    const tax = activeLines.reduce((sum, line) => {
      const rowTaxable = line.quantity * line.unitPrice
      return sum + rowTaxable * (line.taxPercent / 100)
    }, 0)
    return { subTotal, tax, discount, net: taxable + tax }
  }, [lines, deletedRowIndices])

  const updateLine = (idx: number, patch: Partial<OrderLine>) => {
    setLines((prev) => prev.map((line, i) => (i === idx ? { ...line, ...patch } : line)))
  }

  const addRows = (count: number) => {
    const defaultTax = getDefaultTaxRate(taxRates)
    setLines((prev) => [...prev, ...Array.from({ length: count }, () => newLine(defaultTax || undefined))])
  }

  const removeLine = (idx: number) => {
    const line = lines[idx]
    if (line && Number(line.id || 0) > 0) {
      setDeletedRowIndices((prev) => {
        const next = new Set(prev)
        if (next.has(idx)) next.delete(idx)
        else next.add(idx)
        return next
      })
      return
    }
    setLines((prev) => prev.filter((_, i) => i !== idx))
    setDeletedRowIndices((prev) => {
      const next = new Set<number>()
      prev.forEach((oldIndex) => {
        if (oldIndex < idx) next.add(oldIndex)
        else if (oldIndex > idx) next.add(oldIndex - 1)
      })
      return next
    })
  }

  const toIsoDate = (value: string) => {
    if (!value) return new Date().toISOString()
    return new Date(`${value}T00:00:00`).toISOString()
  }

  const handleSave = async () => {
    if (isEdit && hasGrn) return
    if (!branchId) {
      toast.error(t('purchaseOrders.pleaseSelectBranch'))
      return
    }
    if (!vendorId) {
      toast.error(t('purchaseOrders.pleaseSelectVendor'))
      return
    }
    if (!warehouseId) {
      toast.error(t('purchaseOrders.pleaseSelectWarehouse'))
      return
    }

    const validLines = lines
      .map((line, index) => ({ line, index }))
      .filter(({ line, index }) => {
        const isDeleted = deletedRowIndices.has(index)
        const hasData = Number(line.itemId || 0) > 0 && Number(line.quantity || 0) > 0
        const hasPersistentId = Number(line.id || 0) > 0
        if (isDeleted) return hasPersistentId
        return hasData
      })
      .map(({ line, index }) => {
        const isDeleted = deletedRowIndices.has(index)
        const rowSub = Number(line.quantity || 0) * Number(line.unitPrice || 0)
        const rowDiscAmount = 0
        const rowTaxable = rowSub
        const rowTaxAmount = rowTaxable * (Number(line.taxPercent || 0) / 100)
        const rowTotal = rowTaxable + rowTaxAmount
        const row: PurchaseOrderSaveLine = {
          id: isEdit ? Number(line.id || 0) : 0,
          itemId: Number(line.itemId || 0),
          warehouseId: Number(warehouseId || 0),
          quantity: Number(line.quantity || 0),
          quantityInTransit: 0,
          unitPrice: Number(line.unitPrice || 0),
          taxableAmount: Number(rowTaxable.toFixed(2)),
          tax: Number(line.taxId || 0),
          taxAmount: Number(rowTaxAmount.toFixed(2)),
          taxPercent: Number(line.taxPercent || 0),
          discount: 0,
          discountAmount: Number(rowDiscAmount.toFixed(2)),
          lineAmount: Number(rowTotal.toFixed(2)),
          isDeleted: isDeleted ? 'true' : 'false',
        }
        return row
      })

    if (validLines.length === 0) {
      toast.error(t('purchaseOrders.atLeastOneLine'))
      return
    }

    try {
      const vendorName = vendors.find((v) => String(v.id) === String(vendorId))?.name ?? ''
      const warehouseName = warehouses.find((w) => String(w.id) === String(warehouseId))?.name ?? ''
      const orderDateIso = toIsoDate(orderDate)
      const nowIso = new Date().toISOString()
      const refTrim = (reference || '').trim()
      const codeForApi =
        orderCode.trim() || refTrim || vendorName.trim() || `PO-${Date.now()}`

      const payload: PurchaseOrderSavePayload = {
        id: isEdit ? Number(id || 0) : 0,
        code: codeForApi,
        vendorId: Number(vendorId || 0),
        vendorName,
        date: orderDateIso,
        branchId: Number(branchId || 0),
        warehouseId: Number(warehouseId || 0),
        warehouseName,
        reference: reference || '',
        orderDate: orderDateIso,
        deliveryDate: toIsoDate(expectedDate),
        deliveryAddress: deliveryAddress || '',
        paymentTerms: Number(paymentTerms || 0),
        termsAndConditions: '',
        attachmentPath: attachmentFile ? '' : savedAttachmentPath,
        taxableAmount: Number((totals.subTotal - totals.discount).toFixed(2)),
        taxAmount: Number(totals.tax.toFixed(2)),
        discount: Number(totals.discount.toFixed(2)),
        total: Number(totals.net.toFixed(2)),
        status: 'Draft',
        createdDate: isEdit ? createdDate : nowIso,
        createdBy: isEdit ? createdBy : 0,
        modifiedDate: nowIso,
        modifiedBy: 0,
        attachmentFile,
        itemList: validLines,
      }

      console.log('%c=== Purchase Order Save (multipart + purchaseOrderLinesJson) ===', 'background:#0f172a;color:#fff;padding:4px 8px;font-weight:700;')
      console.log('%cMode:', 'font-weight:700;color:#2563eb;', isEdit ? 'UPDATE' : 'SAVE')
      console.log('%cEndpoint:', 'font-weight:700;color:#16a34a;', isEdit ? '/api/PurchaseOrders/Update' : '/api/PurchaseOrders/Save')
      console.log('%cMaster + itemList:', 'font-weight:700;color:#7c3aed;', payload)
      console.log('%cLines JSON:', 'font-weight:700;color:#0d9488;', JSON.stringify(validLines))

      if (isEdit) {
        await purchaseOrderService.update(payload)
      } else {
        await purchaseOrderService.save(payload)
      }

      await showSaveSuccess(isEdit, isEdit ? t('purchaseOrders.updatedSuccess') : t('purchaseOrders.savedSuccess'))
      navigate('/inventory/purchase-orders')
    } catch (error: any) {
      await showSaveError(error, isEdit)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FileText className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('purchaseOrders.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? t('purchaseOrders.subtitleEdit') : t('purchaseOrders.subtitleNew')}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('purchaseOrders.orderDetails')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label>{t('purchaseOrders.orderDate')}</Label>
            <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseOrders.reference')}</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={t('purchaseOrders.placeholderReference')}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseOrders.vendors')}</Label>
            <Select value={vendorId || undefined} onValueChange={setVendorId}>
              <SelectTrigger>
                <SelectValue placeholder={t('purchaseOrders.selectVendor')} />
              </SelectTrigger>
              <SelectContent searchable>
                {vendors.map((v) => (
                  <SelectItem key={v.id} value={String(v.id)}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseOrders.warehouse')}</Label>
            <Select value={warehouseId || undefined} onValueChange={setWarehouseId}>
              <SelectTrigger>
                <SelectValue placeholder={t('purchaseOrders.selectWarehouse')} />
              </SelectTrigger>
              <SelectContent searchable>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={String(w.id)}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseOrders.estimatedDeliveryDate')}</Label>
            <Input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseOrders.deliveryAddress')}</Label>
            <Input value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} />
          </div>
          <div className="space-y-2 md:col-span-1">
            <Label>{t('purchaseOrders.paymentTerms')}</Label>
            <Select value={paymentTerms || undefined} onValueChange={setPaymentTerms}>
              <SelectTrigger>
                <SelectValue placeholder={t('purchaseOrders.selectPaymentTerms')} />
              </SelectTrigger>
              <SelectContent searchable>
                {paymentTermOptions.map((term) => (
                  <SelectItem key={term.id} value={String(term.id)}>
                    {term.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>{t('purchaseOrders.file')}</Label>
            <Input
              type="file"
              onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
              className="cursor-pointer"
            />
            {attachmentFile && (
              <p className="text-xs text-muted-foreground">
                {t('purchaseOrders.selectedFile', { name: attachmentFile.name })}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('purchaseOrders.orderItems')}</CardTitle>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => addRows(1)}>
              <Plus className="mr-1 h-4 w-4" /> {t('purchaseOrders.addRow')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addRows(3)}>
              {t('purchaseOrders.add3Rows')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addRows(5)}>
              {t('purchaseOrders.add5Rows')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b bg-primary/5">
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.item')}</th>
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.quantity')}</th>
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.unitPrice')}</th>
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.taxableAmount')}</th>
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.tax')}</th>
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.taxAmount')}</th>
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.total')}</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => {
                const isDeleted = deletedRowIndices.has(idx)
                const rowSub = line.quantity * line.unitPrice
                const rowTaxable = rowSub
                const rowTax = rowTaxable * (line.taxPercent / 100)
                const rowTotal = rowTaxable + rowTax
                return (
                  <tr key={`${line.id}-${idx}`} className={`border-b ${isDeleted ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                    <td className="p-2">
                      <Select
                        value={line.itemId || undefined}
                        onValueChange={(v) => {
                          const it = items.find((i) => String(i.id) === v)
                          const purchaseTaxId = it?.tax && it.tax > 0 ? String(it.tax) : ''
                          const tax = taxRates.find((x) => String(x.id) === purchaseTaxId)
                          const fallback = getDefaultTaxRate(taxRates)
                          updateLine(idx, {
                            itemId: v,
                            taxId: tax ? purchaseTaxId : fallback ? String(fallback.id) : '',
                            taxPercent: tax ? tax.percent : fallback?.percent || 0,
                          })
                        }}
                        disabled={isDeleted}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('purchaseOrders.selectItem')} />
                        </SelectTrigger>
                        <SelectContent searchable>
                          {items.map((item) => (
                            <SelectItem key={item.id} value={String(item.id)}>
                              {item.code ? `${item.code} - ${item.name}` : item.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-2">
                      <Input type="number" value={line.quantity || ''} disabled={isDeleted} onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) || 0 })} />
                    </td>
                    <td className="p-2">
                      <Input type="number" value={line.unitPrice || ''} disabled={isDeleted} onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) || 0 })} />
                    </td>
                    <td className="p-2">
                      <Input readOnly value={rowTaxable.toFixed(2)} />
                    </td>
                    <td className="p-2">
                      <Select
                        value={line.taxId || undefined}
                        disabled={isDeleted}
                        onValueChange={(v) => {
                          const tax = taxRates.find((x) => String(x.id) === v)
                          updateLine(idx, { taxId: v, taxPercent: tax?.percent || 0 })
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('purchaseOrders.selectTax')} />
                        </SelectTrigger>
                        <SelectContent searchable>
                          {taxRates.map((tax) => (
                            <SelectItem key={tax.id} value={String(tax.id)}>
                              {tax.name} ({tax.percent}%)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-2">
                      <Input readOnly value={rowTax.toFixed(2)} />
                    </td>
                    <td className="p-2">
                      <Input readOnly value={rowTotal.toFixed(2)} />
                    </td>
                    <td className="p-2 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLine(idx)}
                        className={
                          isDeleted
                            ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                            : 'text-destructive hover:text-destructive hover:bg-destructive/10'
                        }
                        title={isDeleted ? t('purchaseOrders.undo') : t('purchaseOrders.deleteRow')}
                      >
                        {isDeleted ? (
                          <span className="text-xs font-semibold">{t('purchaseOrders.undo')}</span>
                        ) : (
                          <Trash2 className="h-4 w-4 text-destructive" />
                        )}
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <div className="mt-6 rounded-md bg-primary/5 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <Label className="text-sm text-muted-foreground">{t('purchaseOrders.subTotal')}</Label>
                <p className="text-lg font-semibold">{totals.subTotal.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('purchaseOrders.totalTax')}</Label>
                <p className="text-lg font-semibold">{totals.tax.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('purchaseOrders.netAmount')}</Label>
                <p className="text-lg font-semibold">{totals.net.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/inventory/purchase-orders')}>
          {t('common.cancel')}
        </Button>
        {!(isEdit && hasGrn) ? (
          <Button onClick={handleSave}>
            <Save className="mr-2 h-4 w-4" />
            {isEdit ? t('common.update') : t('purchaseOrders.save')}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
